import { useMemo, useState } from 'react';
import type { WhaleRow, WhalesReport } from '@/api/types';
import {
  dash,
  formatCount,
  formatDateTime,
  formatDay,
  formatDaysAgo,
  formatEur,
  formatName,
  formatPercent,
  todayUtc,
} from '@/lib/format';
import { toPlayerId } from '@/lib/player-id';
import { Badge } from '@/ui/Badge';
import { Select } from '@/ui/Select';
import { SortableTh, Table, Td, Th } from '@/ui/Table';
import { useSort, type SortColumns } from '@/ui/use-sort';
import {
  NO_WHALE_FILTERS,
  WHALE_FILTERS,
  channelLabel,
  countryOptions,
  filterWhales,
  preferredChannels,
  rewardShare,
} from './whale-figures';

type WhaleSortKey =
  | 'deposits'
  | 'depositCount'
  | 'ggr'
  | 'ngr'
  | 'bets'
  | 'reward'
  | 'cashback'
  | 'bonus'
  | 'withdrawals'
  | 'share'
  | 'lastActive'
  | 'lastComm';

const SORT_COLUMNS: SortColumns<WhaleRow, WhaleSortKey> = {
  deposits: (row) => row.depositEur,
  depositCount: (row) => row.depositCount,
  ggr: (row) => row.ggrEur,
  ngr: (row) => row.ngrEur,
  bets: (row) => row.betEur,
  reward: (row) => row.rewardEur,
  cashback: (row) => row.cashbackEur,
  bonus: (row) => row.bonusConvertedEur,
  withdrawals: (row) => row.withdrawalEur,
  share: rewardShare,
  lastActive: (row) => row.lastActiveDay,
  lastComm: (row) => row.lastComm?.at ?? null,
};

const COLUMNS = 17;

// Full class names on purpose: Tailwind only generates classes it can see in the source.
const signed = (value: number) => (value < 0 ? 'text-red-700' : '');

function Money({ value }: { value: number }) {
  return <Td className={signed(value)}>{formatEur(value, { cents: false })}</Td>;
}

function playerName(row: WhaleRow): string {
  if (!row.profile) return dash;
  const name = formatName(row.profile);
  return name === dash ? (row.profile.username ?? dash) : name;
}

function LastComm({ row, today }: { row: WhaleRow; today: string }) {
  if (!row.lastComm) return <Td align="left">{dash}</Td>;
  const { channel, name, at } = row.lastComm;
  return (
    <Td align="left" title={formatDateTime(at)}>
      <span className="font-medium">{channelLabel(channel)}</span>
      {name && <span className="text-slate-600"> {name}</span>}
      <span className="text-slate-500"> · {formatDaysAgo(todayUtc(new Date(at)), today)}</span>
    </Td>
  );
}

function PreferredMethod({ row, commsSince }: { row: WhaleRow; commsSince: string }) {
  const preferred = preferredChannels(row.channels);
  if (preferred.length === 0) {
    const sent = row.channels.reduce((total, channel) => total + channel.sent, 0);
    return (
      <Td
        align="left"
        title={
          sent === 0
            ? `No CRM message ${commsSince}.`
            : `None of the ${formatCount(sent)} messages ${commsSince} was followed by a deposit within 24 hours.`
        }
      >
        {dash}
      </Td>
    );
  }
  return (
    <Td
      align="left"
      title={preferred
        .map((channel) => `${channelLabel(channel.channel)}: ${channel.deposited24h} of ${channel.sent} messages`)
        .join('\n')}
    >
      {preferred.map((channel) => `${channelLabel(channel.channel)} (${formatPercent(channel.share)})`).join(' / ')}
    </Td>
  );
}

export function WhalesTable({ report }: { report: WhalesReport }) {
  const [filters, setFilters] = useState(NO_WHALE_FILTERS);
  const countries = useMemo(() => countryOptions(report.rows), [report.rows]);
  const filtered = useMemo(() => filterWhales(report.rows, filters), [report.rows, filters]);
  const { sorted: rows, sort, toggle } = useSort(filtered, SORT_COLUMNS, { key: 'deposits', direction: 'desc' });
  const today = todayUtc();
  const comms = report.activity.comms;
  const commsSince = comms ? `since ${formatDay(comms.from)}` : 'not synced yet';

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-4">
          <Select
            label="Show"
            value={filters.band}
            options={WHALE_FILTERS}
            onChange={(band) => setFilters((current) => ({ ...current, band }))}
          />
          <Select
            label="Country"
            value={filters.country}
            options={[
              { value: '', label: 'All countries' },
              ...countries.map(({ country, players }) => ({ value: country, label: `${country} (${players})` })),
            ]}
            onChange={(country) => setFilters((current) => ({ ...current, country }))}
          />
        </div>
        <p className="text-sm text-slate-500">
          {rows.length} of {report.rows.length} players
        </p>
      </div>
      <Table>
        <thead>
          <tr>
            <Th align="left">Name</Th>
            <Th align="left">Country</Th>
            <Th align="left">Status</Th>
            <SortableTh sortKey="deposits" sort={sort} onSort={toggle}>
              Deposits
            </SortableTh>
            <SortableTh sortKey="depositCount" sort={sort} onSort={toggle}>
              # Dep
            </SortableTh>
            <SortableTh sortKey="ggr" sort={sort} onSort={toggle}>
              GGR
            </SortableTh>
            <SortableTh sortKey="ngr" sort={sort} onSort={toggle}>
              NGR
            </SortableTh>
            <SortableTh sortKey="bets" sort={sort} onSort={toggle}>
              Bets
            </SortableTh>
            <Th align="left" title="The game type with the most real-money bets in the range, and its share of them">
              Main game
            </Th>
            <SortableTh sortKey="reward" sort={sort} onSort={toggle}>
              Reward
            </SortableTh>
            <SortableTh sortKey="cashback" sort={sort} onSort={toggle} title="Cashback paid">
              CB paid
            </SortableTh>
            <SortableTh sortKey="bonus" sort={sort} onSort={toggle} title="Bonus converted to real money">
              Bonus conv
            </SortableTh>
            <SortableTh sortKey="withdrawals" sort={sort} onSort={toggle} title="Withdrawals paid">
              WD paid
            </SortableTh>
            <SortableTh
              sortKey="share"
              sort={sort}
              onSort={toggle}
              title="Reward + cashback + bonus converted, as a share of GGR. A player with no positive GGR is a loss."
            >
              Share %
            </SortableTh>
            <SortableTh align="left" sortKey="lastActive" sort={sort} onSort={toggle}>
              Last active
            </SortableTh>
            <SortableTh
              align="left"
              sortKey="lastComm"
              sort={sort}
              onSort={toggle}
              title={`The latest CRM message, segment inclusion or reward, ${commsSince}`}
            >
              Last comm from us
            </SortableTh>
            <Th
              align="left"
              title={`Likelihood according to what worked historically: of the CRM messages ${commsSince} that a deposit followed within 24 hours, the share each channel sent`}
            >
              Preferred method of communication
            </Th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <Td align="left" colSpan={COLUMNS} className="text-slate-500">
                {report.rows.length === 0 ? 'No player matches this range.' : 'No player matches the filters.'}
              </Td>
            </tr>
          )}
          {rows.map((row) => {
            const share = rewardShare(row);
            return (
              <tr key={row.playerUuid}>
                <Td
                  align="left"
                  title={row.profile ? undefined : 'The players mirror has no profile for this player yet.'}
                >
                  <div>{playerName(row)}</div>
                  <div className="font-mono text-xs text-slate-500">{toPlayerId(row.playerUuid)}</div>
                </Td>
                <Td align="left">{row.country ?? dash}</Td>
                <Td align="left">
                  <Badge value={row.profile?.status ?? null} />
                </Td>
                <Money value={row.depositEur} />
                <Td>{formatCount(row.depositCount)}</Td>
                <Money value={row.ggrEur} />
                <Money value={row.ngrEur} />
                <Money value={row.betEur} />
                <Td align="left">
                  {row.mainGame ? `${row.mainGame.gameType} ${formatPercent(row.mainGame.betShare)}` : dash}
                </Td>
                <Money value={row.rewardEur} />
                <Money value={row.cashbackEur} />
                <Money value={row.bonusConvertedEur} />
                <Money value={row.withdrawalEur} />
                <Td className={share === null ? 'text-red-700' : ''}>
                  {share === null ? 'loss' : formatPercent(share, 2)}
                </Td>
                <Td align="left" title={formatDay(row.lastActiveDay)}>
                  {formatDaysAgo(row.lastActiveDay, today)}
                </Td>
                <LastComm row={row} today={today} />
                <PreferredMethod row={row} commsSince={commsSince} />
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
}
